import * as radixIcons from '@radix-ui/react-icons';
import * as lucideIcons from 'lucide-react';
import {
  ActivityIcon,
  AlignCenter,
  AlignCenterVertical,
  AlignEndVertical,
  AlignJustify,
  AlignLeft,
  AlignRight,
  AlignStartVertical,
  ArrowDownAZ,
  ArrowDownToLine,
  ArrowDownZA,
  AudioLines,
  Ban,
  Bold,
  Bot,
  Braces,
  CircleHelp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Clock,
  Code2,
  Copy,
  CornerDownLeft,
  Download,
  ExpandIcon,
  ExternalLink,
  File,
  FileText,
  Ghost,
  GripVertical,
  Hash,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Highlighter,
  HistoryIcon,
  Image,
  ImagePlus,
  Italic,
  Languages,
  List,
  ListOrdered,
  ListTodo,
  ListTreeIcon,
  Menu,
  MessageSquare,
  MessageSquareMore,
  PenLine,
  MinimizeIcon,
  MoonStar,
  MoreVertical,
  PaintBucket,
  PanelLeftCloseIcon,
  PanelLeftIcon,
  PanelRightIcon,
  Pencil,
  Pin,
  PinOff,
  Play,
  Quote,
  Redo2,
  Repeat,
  Repeat2,
  RotateCcw,
  Save,
  SmilePlus,
  SquareCode,
  StarOff,
  SquareSplitHorizontal,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  Type,
  Underline,
  Undo2,
  Workflow,
  Zap,
} from 'lucide-react';
import type { ReactNode, SVGProps } from 'react';
import { CopilotIcon } from '../../app/icons/copilot';
import { AiSparklesIcon } from './ai-sparkles-icon';
import { AlignBottomIcon } from './align-bottom-icon';
import { AlignTopIcon } from './align-top-icon';
import { CheckAiIcon } from './check-ai-icon';
import { ClearFitlersIcon } from './clear-filters';
import { CompleteSentenceIcon } from './complete-sentence-icon';
import { MicAiIcon } from './mic-ai-icon';
import { RefreshAiIcon } from './refresh-ai-icon';
import { SectionIcon } from './section-icon';
import { Simplify2Icon } from './simplify-2-icon';
import { SmileAiIcon } from './smile-ai-icon';
import { SummarizeTextIcon } from './summarize-text-icon';
import { TextColorSmallIcon } from './text-color-small-icon';
import { TextExtendIcon } from './text-extend-icon';
import { TextReduceIcon } from './text-reduce-icon';

type IconComponent = (props: SVGProps<SVGSVGElement>) => ReactNode;

const HEROICON_TO_LUCIDE_OVERRIDES: Record<string, string> = {
  AcademicCapIcon: 'GraduationCap',
  AdjustmentsHorizontalIcon: 'SlidersHorizontal',
  AdjustmentsVerticalIcon: 'SlidersVertical',
  ArchiveBoxArrowDownIcon: 'ArchiveRestore',
  ArchiveBoxIcon: 'Archive',
  ArchiveBoxXMarkIcon: 'ArchiveX',
  ArrowsPointingOutIcon: 'Expand',
  ArrowDownOnSquareIcon: 'Download',
  ArrowDownOnSquareStackIcon: 'Copy',
  ArrowLeftEndOnRectangleIcon: 'LogOut',
  ArrowPathIcon: 'RefreshCw',
  ArrowRightEndOnRectangleIcon: 'LogIn',
  ArrowRightStartOnRectangleIcon: 'LogIn',
  ArrowsUpDownIcon: 'ArrowUpDown',
  ArrowTopRightOnSquareIcon: 'SquareArrowOutUpRight',
  ArrowTrendingDownIcon: 'TrendingDown',
  ArrowTrendingUpIcon: 'TrendingUp',
  ArrowUpOnSquareIcon: 'Upload',
  ArrowUpOnSquareStackIcon: 'Copy',
  ArrowUpTrayIcon: 'Upload',
  AtSymbolIcon: 'AtSign',
  BackspaceIcon: 'Delete',
  BanknotesIcon: 'Banknote',
  Bars2Icon: 'PanelLeft',
  Bars3BottomRightIcon: 'AlignRight',
  Bars3Icon: 'Menu',
  Bars4Icon: 'LayoutGrid',
  BarsArrowDownIcon: 'ArrowDownToLine',
  BarsArrowUpIcon: 'ArrowUpToLine',
  Battery0Icon: 'Battery',
  Battery50Icon: 'BatteryMedium',
  Battery100Icon: 'BatteryFull',
  BackwardIcon: 'SkipBack',
  BellAlertIcon: 'BellRing',
  BellIcon: 'Bell',
  BellSlashIcon: 'BellOff',
  BellSnoozeIcon: 'BellRing',
  BoltIcon: 'Zap',
  BoltSlashIcon: 'ZapOff',
  BookmarkIcon: 'Bookmark',
  BookmarkSlashIcon: 'BookmarkMinus',
  BookmarkSquareIcon: 'Bookmark',
  BugAntIcon: 'Bug',
  BuildingLibraryIcon: 'Library',
  BuildingOfficeIcon: 'Building',
  BuildingStorefrontIcon: 'Store',
  ChartBarIcon: 'BarChart3',
  ChatBubbleBottomCenterIcon: 'MessageCircle',
  CheckBadgeIcon: 'BadgeCheck',
  ChevronDoubleDownIcon: 'ChevronsDown',
  ChevronDoubleLeftIcon: 'ChevronsLeft',
  ChevronDoubleRightIcon: 'ChevronsRight',
  CircleStackIcon: 'Layers',
  ClipboardDocumentCheckIcon: 'ClipboardCheck',
  ClipboardDocumentIcon: 'Clipboard',
  ClipboardDocumentListIcon: 'ClipboardList',
  ClipboardIcon: 'Clipboard',
  CloudArrowDownIcon: 'CloudDownload',
  CloudArrowUpIcon: 'CloudUpload',
  CloudIcon: 'Cloud',
  Cog6ToothIcon: 'Settings',
  Cog8ToothIcon: 'Settings',
  CogIcon: 'Settings',
  CodeBracketIcon: 'Code',
  CodeBracketSquareIcon: 'Code2',
  CommandLineIcon: 'Terminal',
  ComputerDesktopIcon: 'Monitor',
  CpuChipIcon: 'Cpu',
  CreditCardIcon: 'CreditCard',
  CubeIcon: 'Box',
  CubeTransparentIcon: 'Box',
  CurrencyBangladeshiIcon: 'IndianRupee',
  CurrencyDollarIcon: 'DollarSign',
  CurrencyEuroIcon: 'Euro',
  CurrencyPoundIcon: 'PoundSterling',
  CurrencyRupeeIcon: 'IndianRupee',
  CurrencyYenIcon: 'JapaneseYen',
  CursorArrowRaysIcon: 'MousePointerClick',
  CursorArrowRippleIcon: 'MousePointer2',
  DevicePhoneMobileIcon: 'Smartphone',
  DeviceTabletIcon: 'Tablet',
  DocumentArrowDownIcon: 'FileDown',
  DocumentArrowUpIcon: 'FileUp',
  DocumentChartBarIcon: 'FileBarChart',
  DocumentCheckIcon: 'FileCheck',
  DocumentDuplicateIcon: 'Copy',
  DocumentIcon: 'File',
  DocumentMagnifyingGlassIcon: 'FileSearch',
  DocumentMinusIcon: 'FileMinus',
  DocumentPlusIcon: 'FilePlus',
  DocumentTextIcon: 'FileText',
  EllipsisHorizontalCircleIcon: 'CircleEllipsis',
  EllipsisHorizontalIcon: 'Ellipsis',
  EnvelopeIcon: 'Mail',
  EnvelopeOpenIcon: 'MailOpen',
  ExclamationCircleIcon: 'CircleAlert',
  ExclamationTriangleIcon: 'TriangleAlert',
  EyeDropperIcon: 'Pipette',
  EyeSlashIcon: 'EyeOff',
  FaceFrownIcon: 'Frown',
  FaceSmileIcon: 'Smile',
  FingerPrintIcon: 'Fingerprint',
  FireIcon: 'Flame',
  FolderArrowDownIcon: 'FolderDown',
  ForwardIcon: 'SkipForward',
  GifIcon: 'Image',
  GiftTopIcon: 'Gift',
  GlobeAltIcon: 'Globe',
  GlobeAmericasIcon: 'Globe',
  GlobeAsiaAustraliaIcon: 'Globe',
  GlobeEuropeAfricaIcon: 'Globe',
  HandRaisedIcon: 'Hand',
  HandThumbDownIcon: 'ThumbsDown',
  HandThumbUpIcon: 'ThumbsUp',
  HashtagIcon: 'Hash',
  HomeModernIcon: 'Home',
  IdentificationIcon: 'IdCard',
  InboxArrowDownIcon: 'Inbox',
  InboxStackIcon: 'Inbox',
  InformationCircleIcon: 'Info',
  LanguageIcon: 'Languages',
  LifebuoyIcon: 'LifeBuoy',
  LightBulbIcon: 'Lightbulb',
  LinkIcon: 'Link',
  ListBulletIcon: 'List',
  LockClosedIcon: 'Lock',
  LockOpenIcon: 'LockOpen',
  MagnifyingGlassCircleIcon: 'Search',
  MagnifyingGlassIcon: 'Search',
  MagnifyingGlassMinusIcon: 'ZoomOut',
  MagnifyingGlassPlusIcon: 'ZoomIn',
  MicrophoneIcon: 'Mic',
  MusicalNoteIcon: 'Music',
  NoSymbolIcon: 'Ban',
  PaintBrushIcon: 'Paintbrush',
  PaperAirplaneIcon: 'Send',
  PaperClipIcon: 'Paperclip',
  PencilSquareIcon: 'SquarePen',
  PhoneArrowDownLeftIcon: 'PhoneIncoming',
  PhoneArrowUpRightIcon: 'PhoneOutgoing',
  PhoneXMarkIcon: 'PhoneOff',
  PhotoIcon: 'Image',
  PlayPauseIcon: 'Pause',
  PlusIcon: 'Plus',
  PresentationChartBarIcon: 'BarChart3',
  PresentationChartLineIcon: 'LineChart',
  PuzzlePieceIcon: 'Puzzle',
  QuestionMarkCircleIcon: 'CircleHelp',
  QueueListIcon: 'ListOrdered',
  ReceiptPercentIcon: 'ReceiptCent',
  ReceiptRefundIcon: 'ReceiptText',
  RectangleGroupIcon: 'Layout',
  RectangleStackIcon: 'Layers',
  RocketLaunchIcon: 'Rocket',
  ServerStackIcon: 'Server',
  ShieldCheckIcon: 'ShieldCheck',
  ShieldExclamationIcon: 'ShieldAlert',
  ShoppingBagIcon: 'ShoppingBag',
  ShoppingCartIcon: 'ShoppingCart',
  SignalSlashIcon: 'SignalZero',
  SpeakerWaveIcon: 'Volume2',
  SpeakerXMarkIcon: 'VolumeX',
  Square2StackIcon: 'Copy',
  SquaresPlusIcon: 'SquarePlus',
  StopIcon: 'Square',
  SwatchIcon: 'Palette',
  UserCircleIcon: 'CircleUser',
  UserGroupIcon: 'Users',
  VideoCameraIcon: 'Video',
  VideoCameraSlashIcon: 'VideoOff',
  ViewColumnsIcon: 'PanelLeft',
  ViewfinderCircleIcon: 'Scan',
  WindowIcon: 'Square',
  WrenchScrewdriverIcon: 'Wrench',
  WrenchIcon: 'Wrench',
  XMarkIcon: 'X',
};

const getLucideIconByName = (name: string): IconComponent | undefined => {
  const candidate = (lucideIcons as Record<string, unknown>)[name];
  // Lucide exports React forwardRef components (objects), not plain functions
  if (
    candidate != null &&
    (typeof candidate === 'function' ||
      (typeof candidate === 'object' && 'render' in (candidate as object)))
  ) {
    return candidate as IconComponent;
  }
  return undefined;
};

const resolveIcon = (heroiconName: string): IconComponent => {
  const directName = heroiconName.replace(/Icon$/, '');
  const lucideName = HEROICON_TO_LUCIDE_OVERRIDES[heroiconName] ?? directName;
  return getLucideIconByName(lucideName) ?? CircleHelp;
};

const heroIcon = (name: string): IconComponent => resolveIcon(name);

export const ICON_MAPPING = {
  warning: heroIcon('ExclamationTriangleIcon'),
  'warning-circle': heroIcon('ExclamationCircleIcon'),
  search: heroIcon('MagnifyingGlassIcon'),
  close: heroIcon('XMarkIcon'),
  add: heroIcon('PlusIcon'),
  save: Save,
  edit: Pencil,
  filter: heroIcon('FunnelIcon'),
  copy: Copy,
  refresh: Repeat,
  'bar-chart': heroIcon('ChartBarIcon'),
  'pie-chart': heroIcon('ChartPieIcon'),
  'chat-bubble': heroIcon('ChatBubbleBottomCenterIcon'),
  dollar: heroIcon('CurrencyDollarIcon'),
  lightbulb: heroIcon('LightBulbIcon'),
  plus: heroIcon('PlusIcon'),
  play: Play,
  'lock-open': heroIcon('LockOpenIcon'),
  'lock-closed': heroIcon('LockClosedIcon'),
  clock: heroIcon('ClockIcon'),
  'academic-cap': heroIcon('AcademicCapIcon'),
  'adjustments-horizontal': heroIcon('AdjustmentsHorizontalIcon'),
  'adjustments-vertical': heroIcon('AdjustmentsVerticalIcon'),
  'archive-box-arrow-down': heroIcon('ArchiveBoxArrowDownIcon'),
  'archive-box-x-mark': heroIcon('ArchiveBoxXMarkIcon'),
  'archive-box': heroIcon('ArchiveBoxIcon'),
  'arrow-down-circle': heroIcon('ArrowDownCircleIcon'),
  'arrow-down-left': heroIcon('ArrowDownLeftIcon'),
  'arrow-down-on-square-stack': heroIcon('ArrowDownOnSquareStackIcon'),
  'arrow-down-on-square': heroIcon('ArrowDownOnSquareIcon'),
  'arrow-down': heroIcon('ArrowDownIcon'),
  'arrow-left': heroIcon('ArrowLeftIcon'),
  'arrow-path': heroIcon('ArrowPathIcon'),
  'arrow-right': heroIcon('ArrowRightIcon'),
  'arrow-right-start-on-rectangle': heroIcon('ArrowRightStartOnRectangleIcon'),
  'arrow-top-right-on-square': heroIcon('ArrowTopRightOnSquareIcon'),
  'arrow-trending-down': heroIcon('ArrowTrendingDownIcon'),
  'arrow-trending-up': heroIcon('ArrowTrendingUpIcon'),
  'arrow-up-circle': heroIcon('ArrowUpCircleIcon'),
  'arrow-up-left': heroIcon('ArrowUpLeftIcon'),
  'arrow-up-on-square-stack': heroIcon('ArrowUpOnSquareStackIcon'),
  'arrow-up-on-square': heroIcon('ArrowUpOnSquareIcon'),
  'arrow-up-right': heroIcon('ArrowUpRightIcon'),
  'arrow-up-tray': heroIcon('ArrowUpTrayIcon'),
  'arrow-up': heroIcon('ArrowUpIcon'),
  'arrows-up-down': heroIcon('ArrowsUpDownIcon'),
  'at-symbol': heroIcon('AtSymbolIcon'),
  backspace: heroIcon('BackspaceIcon'),
  backward: heroIcon('BackwardIcon'),
  banknotes: heroIcon('BanknotesIcon'),
  'bars-2': heroIcon('Bars2Icon'),
  'bars-3': heroIcon('Bars3Icon'),
  'bars-4': heroIcon('Bars4Icon'),
  'bars-arrow-down': heroIcon('BarsArrowDownIcon'),
  'bars-arrow-up': heroIcon('BarsArrowUpIcon'),
  'battery-0': heroIcon('Battery0Icon'),
  'battery-100': heroIcon('Battery100Icon'),
  'battery-50': heroIcon('Battery50Icon'),
  beaker: heroIcon('BeakerIcon'),
  'bell-alert': heroIcon('BellAlertIcon'),
  'bell-slash': heroIcon('BellSlashIcon'),
  'bell-snooze': heroIcon('BellSnoozeIcon'),
  bell: heroIcon('BellIcon'),
  'bolt-slash': heroIcon('BoltSlashIcon'),
  bolt: heroIcon('BoltIcon'),
  'book-open': heroIcon('BookOpenIcon'),
  'bookmark-slash': heroIcon('BookmarkSlashIcon'),
  'bookmark-square': heroIcon('BookmarkSquareIcon'),
  bookmark: heroIcon('BookmarkIcon'),
  briefcase: heroIcon('BriefcaseIcon'),
  'bug-ant': heroIcon('BugAntIcon'),
  'building-library': heroIcon('BuildingLibraryIcon'),
  'building-office': heroIcon('BuildingOfficeIcon'),
  'building-storefront': heroIcon('BuildingStorefrontIcon'),
  cake: heroIcon('CakeIcon'),
  calculator: heroIcon('CalculatorIcon'),
  'calendar-days': heroIcon('CalendarDaysIcon'),
  calendar: heroIcon('CalendarIcon'),
  camera: heroIcon('CameraIcon'),
  'chart-bar': heroIcon('ChartBarIcon'),
  'chart-pie': heroIcon('ChartPieIcon'),
  'check-badge': heroIcon('CheckBadgeIcon'),
  'check-circle': heroIcon('CheckCircleIcon'),
  check: heroIcon('CheckIcon'),
  'chevron-double-down': heroIcon('ChevronDoubleDownIcon'),
  'chevron-double-left': heroIcon('ChevronDoubleLeftIcon'),
  'chevron-double-right': heroIcon('ChevronDoubleRightIcon'),
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up-down': ChevronsUpDown,
  'chevron-up': ChevronUp,
  'circle-stack': heroIcon('CircleStackIcon'),
  'clipboard-document-check': heroIcon('ClipboardDocumentCheckIcon'),
  'clipboard-document-list': heroIcon('ClipboardDocumentListIcon'),
  'clipboard-document': heroIcon('ClipboardDocumentIcon'),
  clipboard: heroIcon('ClipboardIcon'),
  'cloud-arrow-down': heroIcon('CloudArrowDownIcon'),
  'cloud-arrow-up': heroIcon('CloudArrowUpIcon'),
  cloud: heroIcon('CloudIcon'),
  'code-bracket-square': heroIcon('CodeBracketSquareIcon'),
  'code-bracket': heroIcon('CodeBracketIcon'),
  'cog-6-tooth': heroIcon('Cog6ToothIcon'),
  'cog-8-tooth': heroIcon('Cog8ToothIcon'),
  cog: heroIcon('CogIcon'),
  'command-line': heroIcon('CommandLineIcon'),
  'computer-desktop': heroIcon('ComputerDesktopIcon'),
  container: radixIcons.ContainerIcon,
  'cpu-chip': heroIcon('CpuChipIcon'),
  'credit-card': heroIcon('CreditCardIcon'),
  'cube-transparent': heroIcon('CubeTransparentIcon'),
  help: heroIcon('QuestionMarkCircleIcon'),
  cube: heroIcon('CubeIcon'),
  'currency-bangladeshi': heroIcon('CurrencyBangladeshiIcon'),
  'currency-dollar': heroIcon('CurrencyDollarIcon'),
  'currency-euro': heroIcon('CurrencyEuroIcon'),
  'currency-pound': heroIcon('CurrencyPoundIcon'),
  'currency-rupee': heroIcon('CurrencyRupeeIcon'),
  'currency-yen': heroIcon('CurrencyYenIcon'),
  'cursor-arrow-rays': heroIcon('CursorArrowRaysIcon'),
  'cursor-arrow-ripple': heroIcon('CursorArrowRippleIcon'),
  'device-phone-mobile': heroIcon('DevicePhoneMobileIcon'),
  'device-tablet': heroIcon('DeviceTabletIcon'),
  'document-arrow-down': heroIcon('DocumentArrowDownIcon'),
  'document-arrow-up': heroIcon('DocumentArrowUpIcon'),
  'document-chart-bar': heroIcon('DocumentChartBarIcon'),
  'document-check': heroIcon('DocumentCheckIcon'),
  'document-duplicate': heroIcon('DocumentDuplicateIcon'),
  'document-magnifying-glass': heroIcon('DocumentMagnifyingGlassIcon'),
  'document-minus': heroIcon('DocumentMinusIcon'),
  'document-plus': heroIcon('DocumentPlusIcon'),
  'document-text': heroIcon('DocumentTextIcon'),
  document: heroIcon('DocumentIcon'),
  'file-text': FileText,
  'ellipsis-horizontal-circle': heroIcon('EllipsisHorizontalCircleIcon'),
  'ellipsis-horizontal': heroIcon('EllipsisHorizontalIcon'),
  'ellipsis-vertical': heroIcon('EllipsisVerticalIcon'),
  'envelope-open': heroIcon('EnvelopeOpenIcon'),
  envelope: heroIcon('EnvelopeIcon'),
  'exclamation-circle': heroIcon('ExclamationCircleIcon'),
  'exclamation-triangle': heroIcon('ExclamationTriangleIcon'),
  'eye-dropper': heroIcon('EyeDropperIcon'),
  'eye-slash': heroIcon('EyeSlashIcon'),
  eye: heroIcon('EyeIcon'),
  'face-frown': heroIcon('FaceFrownIcon'),
  'face-smile': heroIcon('FaceSmileIcon'),
  file: File,
  film: heroIcon('FilmIcon'),
  'finger-print': heroIcon('FingerPrintIcon'),
  fire: heroIcon('FireIcon'),
  flag: heroIcon('FlagIcon'),
  'folder-arrow-down': heroIcon('FolderArrowDownIcon'),
  'folder-minus': heroIcon('FolderMinusIcon'),
  'folder-open': heroIcon('FolderOpenIcon'),
  'folder-plus': heroIcon('FolderPlusIcon'),
  folder: heroIcon('FolderIcon'),
  forward: heroIcon('ForwardIcon'),
  funnel: heroIcon('FunnelIcon'),
  gif: heroIcon('GifIcon'),
  'gift-top': heroIcon('GiftTopIcon'),
  ghost: Ghost,
  gift: heroIcon('GiftIcon'),
  'globe-alt': heroIcon('GlobeAltIcon'),
  'globe-americas': heroIcon('GlobeAmericasIcon'),
  'globe-asia-australia': heroIcon('GlobeAsiaAustraliaIcon'),
  'globe-europe-africa': heroIcon('GlobeEuropeAfricaIcon'),
  'hand-raised': heroIcon('HandRaisedIcon'),
  'hand-thumb-down': heroIcon('HandThumbDownIcon'),
  'hand-thumb-up': heroIcon('HandThumbUpIcon'),
  hashtag: heroIcon('HashtagIcon'),
  heart: heroIcon('HeartIcon'),
  'home-modern': heroIcon('HomeModernIcon'),
  home: heroIcon('HomeIcon'),
  identification: heroIcon('IdentificationIcon'),
  'inbox-arrow-down': heroIcon('InboxArrowDownIcon'),
  'inbox-stack': heroIcon('InboxStackIcon'),
  inbox: heroIcon('InboxIcon'),
  'information-circle': heroIcon('InformationCircleIcon'),
  key: heroIcon('KeyIcon'),
  language: heroIcon('LanguageIcon'),
  lifebuoy: heroIcon('LifebuoyIcon'),
  'light-bulb': heroIcon('LightBulbIcon'),
  'lightning-bolt': radixIcons.LightningBoltIcon,
  link: heroIcon('LinkIcon'),
  'list-bullet': heroIcon('ListBulletIcon'),
  'list-tree': ListTreeIcon,
  'magnifying-glass-circle': heroIcon('MagnifyingGlassCircleIcon'),
  'magnifying-glass-minus': heroIcon('MagnifyingGlassMinusIcon'),
  'magnifying-glass-plus': heroIcon('MagnifyingGlassPlusIcon'),
  'magnifying-glass': heroIcon('MagnifyingGlassIcon'),
  'map-pin': heroIcon('MapPinIcon'),
  map: heroIcon('MapIcon'),
  megaphone: heroIcon('MegaphoneIcon'),
  microphone: heroIcon('MicrophoneIcon'),
  'minus-circle': heroIcon('MinusCircleIcon'),
  minus: heroIcon('MinusIcon'),
  moon: heroIcon('MoonIcon'),
  'musical-note': heroIcon('MusicalNoteIcon'),
  newspaper: heroIcon('NewspaperIcon'),
  'no-symbol': heroIcon('NoSymbolIcon'),
  'paint-brush': heroIcon('PaintBrushIcon'),
  'paper-airplane': heroIcon('PaperAirplaneIcon'),
  'paper-clip': heroIcon('PaperClipIcon'),
  'pause-circle': heroIcon('PauseCircleIcon'),
  pause: heroIcon('PauseIcon'),
  'pencil-square': heroIcon('PencilSquareIcon'),
  pencil: heroIcon('PencilIcon'),
  'phone-arrow-down-left': heroIcon('PhoneArrowDownLeftIcon'),
  'phone-arrow-up-right': heroIcon('PhoneArrowUpRightIcon'),
  'phone-x-mark': heroIcon('PhoneXMarkIcon'),
  phone: heroIcon('PhoneIcon'),
  photo: heroIcon('PhotoIcon'),
  'play-circle': heroIcon('PlayCircleIcon'),
  'play-pause': heroIcon('PlayPauseIcon'),
  'plus-circle': heroIcon('PlusCircleIcon'),
  power: heroIcon('PowerIcon'),
  'presentation-chart-bar': heroIcon('PresentationChartBarIcon'),
  'presentation-chart-line': heroIcon('PresentationChartLineIcon'),
  printer: heroIcon('PrinterIcon'),
  'puzzle-piece': heroIcon('PuzzlePieceIcon'),
  'qr-code': heroIcon('QrCodeIcon'),
  'question-mark-circle': heroIcon('QuestionMarkCircleIcon'),
  'queue-list': heroIcon('QueueListIcon'),
  radio: heroIcon('RadioIcon'),
  'receipt-percent': heroIcon('ReceiptPercentIcon'),
  'receipt-refund': heroIcon('ReceiptRefundIcon'),
  'rectangle-group': heroIcon('RectangleGroupIcon'),
  'rectangle-stack': heroIcon('RectangleStackIcon'),
  'rocket-launch': heroIcon('RocketLaunchIcon'),
  rss: heroIcon('RssIcon'),
  scale: heroIcon('ScaleIcon'),
  scissors: heroIcon('ScissorsIcon'),
  'server-stack': heroIcon('ServerStackIcon'),
  server: heroIcon('ServerIcon'),
  share: heroIcon('ShareIcon'),
  'shield-check': heroIcon('ShieldCheckIcon'),
  'shield-exclamation': heroIcon('ShieldExclamationIcon'),
  'shopping-bag': heroIcon('ShoppingBagIcon'),
  'shopping-cart': heroIcon('ShoppingCartIcon'),
  'signal-slash': heroIcon('SignalSlashIcon'),
  signal: heroIcon('SignalIcon'),
  sparkles: heroIcon('SparklesIcon'),
  'speaker-wave': heroIcon('SpeakerWaveIcon'),
  'speaker-x-mark': heroIcon('SpeakerXMarkIcon'),
  'square-2-stack': heroIcon('Square2StackIcon'),
  'squares-plus': heroIcon('SquaresPlusIcon'),
  star: heroIcon('StarIcon'),
  'stop-circle': heroIcon('StopCircleIcon'),
  stop: heroIcon('StopIcon'),
  sun: heroIcon('SunIcon'),
  swatch: heroIcon('SwatchIcon'),
  'table-cells': Table,
  table: Table,
  tag: heroIcon('TagIcon'),
  ticket: heroIcon('TicketIcon'),
  trash: heroIcon('TrashIcon'),
  trophy: heroIcon('TrophyIcon'),
  truck: heroIcon('TruckIcon'),
  tv: heroIcon('TvIcon'),
  'user-circle': heroIcon('UserCircleIcon'),
  'user-group': heroIcon('UserGroupIcon'),
  'user-minus': heroIcon('UserMinusIcon'),
  'user-plus': heroIcon('UserPlusIcon'),
  user: heroIcon('UserIcon'),
  users: heroIcon('UsersIcon'),
  variable: heroIcon('VariableIcon'),
  'video-camera-slash': heroIcon('VideoCameraSlashIcon'),
  'video-camera': heroIcon('VideoCameraIcon'),
  'view-columns': heroIcon('ViewColumnsIcon'),
  'viewfinder-circle': heroIcon('ViewfinderCircleIcon'),
  wallet: heroIcon('WalletIcon'),
  wifi: heroIcon('WifiIcon'),
  window: heroIcon('WindowIcon'),
  'wrench-screwdriver': heroIcon('WrenchScrewdriverIcon'),
  wrench: heroIcon('WrenchIcon'),
  'x-circle': heroIcon('XCircleIcon'),
  'x-mark': heroIcon('XMarkIcon'),
  'arrows-pointing-out': heroIcon('ArrowsPointingOutIcon'),
  bot: Bot,
  'drag-handle': radixIcons.DragHandleDots2Icon,
  'bars-3-bottom-right': heroIcon('Bars3BottomRightIcon'),
  'arrow-left-end-on-rectangle': heroIcon('ArrowLeftEndOnRectangleIcon'),
  'arrow-right-end-on-rectangle': heroIcon('ArrowRightEndOnRectangleIcon'),
  text: radixIcons.TextIcon,
  copilot: (props: any) => (
    <CopilotIcon {...props} height="100%" width="100%" theme="dark" />
  ),
  'clear-filters': ClearFitlersIcon,
  'panel-left-open': PanelLeftIcon,
  'panel-left-close': PanelLeftCloseIcon,
  'panel-right-open': PanelRightIcon,
  expand: ExpandIcon,
  collapse: MinimizeIcon,
  avatar: heroIcon('UserCircleIcon'),
  grip: GripVertical,
  'pin-off': PinOff,
  pin: Pin,
  /**
   * The `star` glyph with a slash through it — "remove from favorites", as
   * against `star`'s "add". Lucide's, not a Heroicon: Heroicons has no
   * star-slash, and every other negated pair in this map (`bell-slash`,
   * `eye-slash`, `pin-off`) resolves to a Lucide `*Off` for the same reason.
   */
  'star-off': StarOff,
  menu: Menu,
  'sort-asc': ArrowDownZA,
  'sort-desc': ArrowDownAZ,
  settings: heroIcon('Cog6ToothIcon'),
  download: Download,
  workflow: Workflow,
  'clock-reset': Clock,
  zap: Zap,
  activity: ActivityIcon,
  // Text formatting
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strikethrough: Strikethrough,
  code2: Code2,
  subscript: Subscript,
  superscript: Superscript,
  // Headings
  'heading-1': Heading1,
  'heading-2': Heading2,
  'heading-3': Heading3,
  'heading-4': Heading4,
  'heading-5': Heading5,
  'heading-6': Heading6,
  // Lists
  'list-bullet-alt': List,
  'list-ordered': ListOrdered,
  'list-todo': ListTodo,
  // Content blocks
  blockquote: Quote,
  'code-block': SquareCode,
  // Images
  image: Image,
  'image-plus': ImagePlus,
  // Links
  'external-link': ExternalLink,
  // Alignment
  'align-text-left': AlignLeft,
  'align-text-right': AlignRight,
  'align-text-center': AlignCenter,
  'align-text-justify': AlignJustify,
  'align-center-vertical': AlignCenterVertical,
  'align-start-vertical': AlignStartVertical,
  'align-end-vertical': AlignEndVertical,
  // Editor actions
  highlighter: Highlighter,
  'paint-bucket': PaintBucket,
  'rotate-ccw': RotateCcw,
  redo: Redo2,
  undo: Undo2,
  repeat: Repeat2,
  'corner-down-left': CornerDownLeft,
  // UI elements
  'smile-plus': SmilePlus,
  'message-square': MessageSquare,
  'message-square-more': MessageSquareMore,
  'pen-line': PenLine,
  'more-vertical': MoreVertical,
  'ellipsis-vertical-alt': MoreVertical,
  languages: Languages,
  type: Type,
  ban: Ban,
  'arrow-down-to-line': ArrowDownToLine,
  'moon-star': MoonStar,
  // Unique tiptap icons
  'align-top': AlignTopIcon,
  'align-bottom': AlignBottomIcon,
  section: SectionIcon,
  'text-color-small': TextColorSmallIcon,
  'text-extend': TextExtendIcon,
  'text-reduce': TextReduceIcon,
  // AI icons
  'ai-sparkles': AiSparklesIcon,
  'check-ai': CheckAiIcon,
  'complete-sentence': CompleteSentenceIcon,
  'mic-ai': MicAiIcon,
  'refresh-ai': RefreshAiIcon,
  'simplify-2': Simplify2Icon,
  'smile-ai': SmileAiIcon,
  'summarize-text': SummarizeTextIcon,
  braces: Braces,
  hash: Hash,
  'square-split-horizontal': SquareSplitHorizontal,
  history: HistoryIcon,
  upload: radixIcons.UploadIcon,
  integrations: Workflow,
  audio: AudioLines,
  skip: lucideIcons.SkipForward,
  credits: lucideIcons.Coins,
  agent: Bot,
  dataset: lucideIcons.Database,
  // Keyed to match RESOURCE_TYPE_ICON, so an action that creates a resource can
  // wear the same icon the library and the workflow canvas give that resource.
  chat: lucideIcons.MessagesSquare,
  trigger: Clock,
  // Aliases for names already reached for at call sites. An unmapped name
  // resolves to `() => null` and renders as empty space, which in a list reads
  // as a heading rather than a row.
  database: lucideIcons.Database,
  publish: heroIcon('CloudArrowUpIcon'),
  'refresh-cw': lucideIcons.RefreshCw,
  waypoints: lucideIcons.Waypoints,
  x: heroIcon('XMarkIcon'),
};

export type IconName = keyof typeof ICON_MAPPING;
